// React/finance-app/src/features/gateway-management/components/GatewayPortsTable.jsx
/**
 * Gateway Ports Table - User View
 * Displays gateway ports in the same style as AdvicePasswordsTable
 * Shows: Site, Email, Password, Numbers, Status, Actions, Messages
 */
import React, { useState, useRef, useEffect } from 'react';
import api from '../../../api';
import Secret from '../../user-dashboard/components/Secret';
import MessageCarousel from '../../user-dashboard/components/MessageCarousel';
import StatusBadgeWithTrafficLight from '../../user-dashboard/components/StatusBadgeWithTrafficLight';
import SearchableSelect from '../../user-dashboard/components/SearchableSelect';
import { copyToClipboard } from '../../passwords/utils/copyToClipboard';
import ConvertToTtOtpModal from '../../user-dashboard/components/ConvertToTtOtpModal';
import {
  isServiceNumber,
  shouldShowActivate,
  isSimConvertedYes,
  canRequestSimConversion,
  isSimConversionRequested,
} from '../../../shared/portActivation';

export default function GatewayPortsTable({
  gatewayName,
  ports = [],
  advicePasswords = [], // Array of advice passwords to match by email
  portStatus = {},
  portMessages = {},
  portCountdowns = {},
  clearPortMessage,
  getButtonState,
  getStatusBadge,
  onActivatePort,
  onReportProblem,
  onEditPort,
  onDeletePort,
  onUpdateEmail, // Callback to update email (user side)
  onUpdatePassword, // Callback to update password (user side)
  onUpdatePort, // Callback to update port number/phone (user side)
  onSendTest, // Callback to send test message (phone number, portId)
  testStatus = {}, // Map of portId -> test status ('sent', 'confirmed', 'failed')
  lastCreatedPortId = null, // ID of most recently created port - only show test button on this port
  isStaff = false, // If true, show additional staff columns
  sites = [], // List of available sites for dropdown
  showUserCode = false, // If true, show User Code column (only for My Sim Box page)
  assignableUsers = [], // List of users that can be assigned ports (for User Code editing)
  currentUserCode = '', // Current logged-in user's user_code (gateway owner)
  onRequestSimConversion = null,
}) {
  const [conversionModal, setConversionModal] = useState({ open: false, port: null });
  const [columnWidths, setColumnWidths] = useState({
    portNumber: 100,
    gateway: 150,
    site: 160,
    email: 220,
    password: 200,
    numbers: 180,
    userCode: 120,
    status: 120,
    actions: 170,
    messages: 320,
    details: 120,
    edit: 160,
    slackId: 120,
    lastMessage: 120,
    daysSince: 100,
  });
  
  // Editing state (user side only) - row-based editing
  const [editingRowId, setEditingRowId] = useState(null); // adviceId of row being edited
  const [editDraft, setEditDraft] = useState({ 
    email: '', 
    password: '',
    port_number: '',
    assigned_number: '',
    site: '',
    target_user_code: '' // For User Code reassignment
  });

  const tableRef = useRef(null);
  const stickyScrollRef = useRef(null);
  const phantomRef = useRef(null);
  const [resizing, setResizing] = useState(null);
  const [sendingTest, setSendingTest] = useState(new Set()); // Track which ports are sending tests

  // Sync sticky scrollbar with the table's horizontal scroll
  useEffect(() => {
    const tableEl = tableRef.current;
    const stickyEl = stickyScrollRef.current;
    const phantomEl = phantomRef.current;
    if (!tableEl || !stickyEl || !phantomEl) return;

    const updatePhantomWidth = () => {
      phantomEl.style.width = tableEl.scrollWidth + 'px';
    };
    updatePhantomWidth();

    let syncing = false;
    const onTableScroll = () => {
      if (syncing) return;
      syncing = true;
      stickyEl.scrollLeft = tableEl.scrollLeft;
      syncing = false;
    };
    const onStickyScroll = () => {
      if (syncing) return;
      syncing = true;
      tableEl.scrollLeft = stickyEl.scrollLeft;
      syncing = false;
    };

    tableEl.addEventListener('scroll', onTableScroll);
    stickyEl.addEventListener('scroll', onStickyScroll);

    const ro = new ResizeObserver(updatePhantomWidth);
    ro.observe(tableEl);

    return () => {
      tableEl.removeEventListener('scroll', onTableScroll);
      stickyEl.removeEventListener('scroll', onStickyScroll);
      ro.disconnect();
    };
  }, []);

  // Create map of email -> advice password
  const adviceByEmail = React.useMemo(() => {
    const map = new Map();
    (advicePasswords || []).forEach((advice) => {
      const key = (advice.email || '').trim().toLowerCase();
      if (key) {
        map.set(key, advice);
      }
    });
    return map;
  }, [advicePasswords]);

  // Group ports by email and match with advice passwords
  // Also include ports without emails as separate rows
  const portRows = React.useMemo(() => {
    const emailMap = new Map();
    const portsWithoutEmail = [];
    
    (ports || []).forEach((port) => {
      const emailKey = (port.user_email || '').trim().toLowerCase();
      if (!emailKey) {
        // Port without email - add as separate row
        portsWithoutEmail.push(port);
        return;
      }
      
      if (!emailMap.has(emailKey)) {
        emailMap.set(emailKey, []);
      }
      emailMap.get(emailKey).push(port);
    });

    // Create rows: one per unique email, with linked ports
    const rows = [];
    emailMap.forEach((portList, emailKey) => {
      const advice = adviceByEmail.get(emailKey);
      rows.push({
        email: portList[0].user_email,
        site: advice?.site || '—',
        password: advice?.password || '',
        ports: portList.sort((a, b) => {
          const av = (a.port_number || '').toString();
          const bv = (b.port_number || '').toString();
          return av.localeCompare(bv, undefined, { numeric: true });
        }),
        adviceId: advice?.id,
      });
    });

    // Add ports without emails as separate rows (one port per row)
    portsWithoutEmail.forEach((port) => {
      rows.push({
        email: '',
        site: '—',
        password: '',
        ports: [port],
        adviceId: null,
      });
    });

    return rows;
  }, [ports, adviceByEmail]);

  // Group rows by ownership (My Ports vs Assigned to Others) when showUserCode
  const groupedRows = React.useMemo(() => {
    if (!showUserCode || !currentUserCode) {
      return { myPorts: portRows, otherPorts: [] };
    }
    const myPorts = [];
    const otherPorts = [];
    portRows.forEach((row) => {
      const hasOtherUsers = row.ports?.some(
        (port) => port.user_code && port.user_code !== currentUserCode
      );
      if (hasOtherUsers) otherPorts.push(row);
      else myPorts.push(row);
    });
    return { myPorts, otherPorts };
  }, [portRows, showUserCode, currentUserCode]);

  const getRowStyle = (row) => {
    if (!showUserCode || !currentUserCode || !row.ports?.length) return {};
    const hasPendingTest = row.ports.some((port) => testStatus[port.id] === 'sent');
    if (hasPendingTest) {
      return {
        backgroundColor: '#ffeb3b',
        borderLeft: '8px solid #ffc107',
        borderRight: '4px solid #ffc107',
        fontWeight: '600',
        boxShadow: 'inset 0 0 0 1px rgba(255, 193, 7, 0.4)',
      };
    }
    const assignedToOthers = row.ports.some(
      (port) => port.user_code && port.user_code !== currentUserCode
    );
    const allMine = row.ports.every(
      (port) => !port.user_code || port.user_code === currentUserCode
    );
    if (assignedToOthers) {
      return { backgroundColor: '#e3f2fd', borderLeft: '4px solid #2196f3' };
    }
    if (allMine) {
      return { backgroundColor: '#f1f8e9', borderLeft: '4px solid #8bc34a' };
    }
    return {};
  };

  const renderSectionHeader = (title, count) => (
    <tr key={`section-${title}`} className="table-secondary" style={{ backgroundColor: '#e9ecef' }}>
      <td
        colSpan={isStaff ? 13 : showUserCode ? 10 : 9}
        className="fw-bold py-2"
        style={{ fontSize: '14px', borderTop: '2px solid #dee2e6' }}
      >
        <span className="me-2">{title}</span>
        <span className="badge bg-secondary">{count}</span>
      </td>
    </tr>
  );

  // Flat list for tbody: section headers + rows (when showUserCode) or just portRows
  const rowsToRender = React.useMemo(() => {
    if (!showUserCode || !currentUserCode || (groupedRows.myPorts.length === 0 && groupedRows.otherPorts.length === 0)) {
      return portRows.map((row) => ({ type: 'row', row }));
    }
    const list = [];
    if (groupedRows.myPorts.length > 0) {
      list.push({ type: 'section', title: '👤 My SIM Ports', count: groupedRows.myPorts.length });
      groupedRows.myPorts.forEach((row) => list.push({ type: 'row', row }));
    }
    if (groupedRows.otherPorts.length > 0) {
      list.push({ type: 'section', title: '👥 Assigned to Other Users', count: groupedRows.otherPorts.length });
      groupedRows.otherPorts.forEach((row) => list.push({ type: 'row', row }));
    }
    return list;
  }, [showUserCode, currentUserCode, groupedRows.myPorts, groupedRows.otherPorts.length, portRows]);

  const handleResizeStart = (column, e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = columnWidths[column];
    setResizing({ column, startX, startWidth });

    const handleMouseMove = (e) => {
      const delta = e.clientX - startX;
      const newWidth = Math.max(60, startWidth + delta);
      setColumnWidths((prev) => ({
        ...prev,
        [column]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      setResizing(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const getDaysSinceLastMessage = (lastMessageSent) => {
    if (!lastMessageSent) return null;
    try {
      const lastDate = new Date(lastMessageSent);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      lastDate.setHours(0, 0, 0, 0);
      const diffTime = today - lastDate;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };

  if (ports.length === 0) {
    return (
      <div className="mb-4">
        <h5 className="mb-3">
          <span className="badge bg-info me-2">Gateway:</span>
          {gatewayName}
        </h5>
        <div className="alert alert-info">
          No ports configured yet. Add your first port above.
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <h5 className="mb-3">
        <span className="badge bg-info me-2">Gateway:</span>
        {gatewayName}
      </h5>
      {showUserCode && currentUserCode && (groupedRows.myPorts.length > 0 || groupedRows.otherPorts.length > 0) && (
        <div className="card mb-3" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="card-body py-2">
            <div className="row g-2">
              <div className="col-md-6">
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-success" style={{ fontSize: '0.9rem', padding: '0.5rem 0.75rem' }}>
                    👤 My Ports
                  </span>
                  <span className="fs-5 fw-bold">{groupedRows.myPorts.length}</span>
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-info" style={{ fontSize: '0.9rem', padding: '0.5rem 0.75rem' }}>
                    👥 Assigned to Others
                  </span>
                  <span className="fs-5 fw-bold">{groupedRows.otherPorts.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <style>
        {`
          .resizable-header {
            position: relative;
            user-select: none;
          }
          .resize-handle {
            position: absolute;
            right: 0;
            top: 0;
            bottom: 0;
            width: 8px;
            cursor: col-resize;
            background: transparent;
            z-index: 1;
          }
          .resize-handle:hover {
            background: rgba(0, 123, 255, 0.15);
          }
          .resizable-table {
            table-layout: fixed;
            width: 100%;
          }
          .resizable-table th,
          .resizable-table td {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            vertical-align: middle;
          }
          .resizable-table td > div {
            overflow: visible;
          }
        `}
      </style>
      <div className="table-responsive" ref={tableRef}>
        <table className="table table-striped table-bordered align-middle resizable-table">
          <thead>
            <tr>
              {/* Port Number - User side only */}
              {!isStaff && (
                <th className="resizable-header" style={{ width: columnWidths.portNumber }}>
                  <div>Port</div>
                  <div
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart('portNumber', e)}
                  />
                </th>
              )}
              {/* Gateway - Staff side only */}
              {isStaff && (
                <th className="resizable-header" style={{ width: columnWidths.gateway }}>
                  <div>Gateway</div>
                  <div
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart('gateway', e)}
                  />
                </th>
              )}
              {/* Port Number - Staff side */}
              {isStaff && (
                <th className="resizable-header" style={{ width: columnWidths.portNumber }}>
                  <div>Port</div>
                  <div
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart('portNumber', e)}
                  />
                </th>
              )}
              <th className="resizable-header" style={{ width: columnWidths.site }}>
                <div>Site</div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart('site', e)}
                />
              </th>
              <th className="resizable-header" style={{ width: columnWidths.email }}>
                <div>Email</div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart('email', e)}
                />
              </th>
              <th className="resizable-header" style={{ width: columnWidths.password }}>
                <div>Password</div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart('password', e)}
                />
              </th>
              <th className="resizable-header" style={{ width: columnWidths.numbers }}>
                <div>Numbers</div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart('numbers', e)}
                />
              </th>
              {showUserCode && (
                <th className="resizable-header" style={{ width: columnWidths.userCode }}>
                  <div>User Code</div>
                  <div
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart('userCode', e)}
                  />
                </th>
              )}
              <th className="resizable-header text-center" style={{ width: columnWidths.status }}>
                <div>Status</div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart('status', e)}
                />
              </th>
              <th className="resizable-header text-center" style={{ width: columnWidths.actions }}>
                <div>Actions</div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart('actions', e)}
                />
              </th>
              <th className="resizable-header" style={{ width: columnWidths.messages }}>
                <div>Recent Messages</div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart('messages', e)}
                />
              </th>
              {isStaff && (
                <>
                  <th className="resizable-header" style={{ width: columnWidths.slackId }}>
                    <div>Slack ID</div>
                    <div
                      className="resize-handle"
                      onMouseDown={(e) => handleResizeStart('slackId', e)}
                    />
                  </th>
                  <th className="resizable-header" style={{ width: columnWidths.lastMessage }}>
                    <div>Last Message</div>
                    <div
                      className="resize-handle"
                      onMouseDown={(e) => handleResizeStart('lastMessage', e)}
                    />
                  </th>
                  <th className="resizable-header" style={{ width: columnWidths.daysSince }}>
                    <div>Days Since</div>
                    <div
                      className="resize-handle"
                      onMouseDown={(e) => handleResizeStart('daysSince', e)}
                    />
                  </th>
                </>
              )}
              <th className="resizable-header" style={{ width: columnWidths.edit }}>
                <div>Edit</div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart('edit', e)}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {portRows.length === 0 ? (
              <tr>
                <td colSpan={isStaff ? 13 : 9} className="text-muted">
                  No ports configured yet.
                </td>
              </tr>
            ) : (
              rowsToRender.map((item, idx) => {
                if (item.type === 'section') {
                  return renderSectionHeader(item.title, item.count);
                }
                const row = item.row;
                const linkedPorts = row.ports || [];
                const editKey = row.adviceId || row.email || `port-${row.ports[0]?.id}`;
                const isEditing = !isStaff && editingRowId === editKey;
                const rowKey = `gateway-port-row-${idx}`;
                return (
                  <tr key={rowKey} style={getRowStyle(row)}>
                    {/* Port Number - User side only (editable when row is in edit mode) */}
                    {!isStaff && (
                      <td>
                        {linkedPorts.length > 0 ? (
                          isEditing ? (
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={editDraft.port_number}
                              onChange={(e) => setEditDraft(prev => ({ ...prev, port_number: e.target.value }))}
                              style={{ maxWidth: 100 }}
                            />
                          ) : (
                            <span className="small fw-medium">
                              {linkedPorts[0].port_number || '—'}
                            </span>
                          )
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    )}
                    {/* Gateway - Staff side only */}
                    {isStaff && (
                      <td>
                        <span className="badge bg-info">{gatewayName}</span>
                      </td>
                    )}
                    {/* Port Number - Staff side (show all port numbers) */}
                    {isStaff && (
                      <td>
                        {linkedPorts.length > 0 ? (
                          <div className="d-flex flex-column gap-1">
                            {linkedPorts.map((port) => (
                              <span key={port.id} className="small fw-medium">
                                {port.port_number || '—'}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    )}
                    {/* Site - Editable when row is in edit mode (user side only) */}
                    <td>
                      {!isStaff && isEditing ? (
                        <SearchableSelect
                          value={editDraft.site}
                          onChange={(value) => setEditDraft(prev => ({ ...prev, site: value }))}
                          options={sites.map(site => ({ value: site, label: site }))}
                          placeholder="Site"
                          allowCustom={false}
                          style={{ maxWidth: 160 }}
                        />
                      ) : (
                        <span>{row.site || '—'}</span>
                      )}
                    </td>

                    {/* Email - Editable when row is in edit mode */}
                    <td>
                      {isEditing ? (
                        <input
                          type="email"
                          className="form-control form-control-sm"
                          value={editDraft.email}
                          onChange={(e) => setEditDraft(prev => ({ ...prev, email: e.target.value }))}
                          style={{ maxWidth: 280 }}
                        />
                      ) : (
                        <div className="d-flex align-items-center gap-1">
                          <span style={{ flex: 1 }}>
                            {row.email || '—'}
                          </span>
                          {row.email && (
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              style={{ padding: '0.125rem 0.25rem', fontSize: '0.75rem' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(row.email);
                              }}
                              title="Copy email"
                            >
                              📋
                            </button>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Password - Editable when row is in edit mode */}
                    <td>
                      {isEditing ? (
                        <Secret
                          value={editDraft.password}
                          onChange={(v) => setEditDraft(prev => ({ ...prev, password: v }))}
                          editable={true}
                          onBlur={undefined}
                        />
                      ) : (
                        <Secret value={row.password || ''} />
                      )}
                    </td>

                    {/* Numbers - Editable when row is in edit mode */}
                    <td>
                      {linkedPorts.length === 0 ? (
                        <span className="text-muted small">No numbers</span>
                      ) : (
                        <div className="d-flex flex-column gap-1">
                          {linkedPorts.map((port) => (
                            isEditing ? (
                              <input
                                key={port?.id ?? `port-${idx}`}
                                type="text"
                                className="form-control form-control-sm"
                                value={editDraft.assigned_number}
                                onChange={(e) => setEditDraft(prev => ({ ...prev, assigned_number: e.target.value }))}
                                placeholder="e.g., 447123456789"
                                style={{ maxWidth: 150 }}
                              />
                            ) : (
                              <div
                                key={port?.id ?? `port-${idx}`}
                                className="d-flex flex-column gap-0"
                              >
                                <span className="small fw-medium">
                                  {port?.assigned_number || '—'}
                                </span>
                                {isSimConvertedYes(port) ? (
                                  <span
                                    className="badge"
                                    title="SIM converted to TT OTP"
                                    style={{
                                      backgroundColor: '#6c757d',
                                      color: '#fff',
                                      fontSize: '0.65rem',
                                      fontWeight: 600,
                                      alignSelf: 'flex-start',
                                    }}
                                  >
                                    OTP SIM
                                  </span>
                                ) : isSimConversionRequested(port) ? (
                                  <span
                                    className="badge"
                                    title="Conversion requested"
                                    style={{
                                      backgroundColor: '#fd7e14',
                                      color: '#fff',
                                      fontSize: '0.65rem',
                                      fontWeight: 600,
                                      alignSelf: 'flex-start',
                                    }}
                                  >
                                    Conversion requested
                                    {port.req_sim_conversion_id
                                      ? ` #${port.req_sim_conversion_id}`
                                      : ''}
                                  </span>
                                ) : isServiceNumber(port) ? (
                                  <span
                                    className="badge"
                                    title="Service number"
                                    style={{
                                      backgroundColor: '#0dcaf0',
                                      color: '#000',
                                      fontSize: '0.65rem',
                                      fontWeight: 600,
                                      alignSelf: 'flex-start',
                                    }}
                                  >
                                    Service
                                  </span>
                                ) : null}
                              </div>
                            )
                          ))}
                        </div>
                      )}
                    </td>

                    {/* User Code - Shows assigned user for each port (only on My Sim Box page) */}
                    {showUserCode && (
                      <td>
                        {linkedPorts.length === 0 ? (
                          <span className="text-muted small">—</span>
                        ) : (
                          <div className="d-flex flex-column gap-1">
                            {isEditing ? (
                              <select
                                className="form-select form-select-sm"
                                value={editDraft.target_user_code || linkedPorts[0]?.user_code || ''}
                                onChange={(e) => setEditDraft(prev => ({ ...prev, target_user_code: e.target.value }))}
                                style={{ maxWidth: 150 }}
                              >
                                <option value="">Assign to me (gateway owner)</option>
                                {assignableUsers.map((user) => (
                                  <option 
                                    key={user.user_code} 
                                    value={user.user_code}
                                  >
                                    {user.user_code}{user.user_code === linkedPorts[0]?.user_code ? ' (current)' : ''}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              linkedPorts.map((port) => (
                                <span
                                  key={port?.id ?? `usercode-${idx}`}
                                  className="small fw-medium"
                                  title={`Assigned to: ${port?.user_code || 'Not assigned'}`}
                                >
                                  {port?.user_code || '—'}
                                </span>
                              ))
                            )}
                          </div>
                        )}
                      </td>
                    )}

                    {/* Status */}
                    <td className="text-center">
                      {linkedPorts.length === 0 ? (
                        <span className="text-muted small">—</span>
                      ) : (
                        <div className="d-flex flex-column gap-1">
                          {linkedPorts.map((port) => (
                            <div key={port?.id ?? `status-${idx}`}>
                              {getStatusBadge ? (
                                getStatusBadge(port)
                              ) : (
                                <StatusBadgeWithTrafficLight
                                  port={port}
                                  portStatus={portStatus}
                                  portMessages={portMessages}
                                  portCountdowns={portCountdowns}
                                  clearPortMessage={clearPortMessage}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="text-center">
                      {linkedPorts.length === 0 ? (
                        <span className="text-muted small">—</span>
                      ) : (
                        <div className="d-flex flex-column gap-1">
                          {linkedPorts.map((port) => {
                            const btnState = getButtonState
                              ? getButtonState(port)
                              : {
                                  disabled: true,
                                  className: 'btn btn-sm btn-outline-secondary',
                                  text: 'N/A',
                                  title: '',
                                };
                            const portTestStatus = testStatus[port.id] || null;
                            const isSendingTestForPort = sendingTest.has(port.id);
                            
                            return (
                              <div
                                key={port?.id ?? `btn-${idx}`}
                                className="d-flex flex-column gap-1"
                              >
                                <div className="d-flex gap-1">
                                  {shouldShowActivate(port) && (
                                    <button
                                      className={btnState.className}
                                      disabled={btnState.disabled}
                                      onClick={() => onActivatePort && onActivatePort(port.id, port.assigned_number)}
                                      title={btnState.title}
                                      style={{
                                        minWidth: '80px',
                                        fontSize: '0.75rem',
                                      }}
                                    >
                                      {btnState.text}
                                    </button>
                                  )}
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => onReportProblem && onReportProblem(port)}
                                    title="Report a problem with this number"
                                    style={{
                                      fontSize: '0.75rem',
                                      padding: '0.25rem 0.4rem',
                                    }}
                                  >
                                    🚨
                                  </button>
                                </div>
                                {!!onRequestSimConversion && canRequestSimConversion(port) && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-warning"
                                    onClick={() => setConversionModal({ open: true, port })}
                                    title="Request conversion of this SIM to a TT OTP number"
                                    style={{
                                      fontSize: '0.7rem',
                                      fontWeight: 700,
                                      whiteSpace: 'nowrap',
                                      borderWidth: 2,
                                    }}
                                  >
                                    Convert to TT OTP
                                  </button>
                                )}
                                {/* Test Confirmation Buttons - show when test status is 'sent' */}
                                {!isStaff && portTestStatus === 'sent' && (
                                  <div className="d-flex flex-column gap-1">
                                    <div className="small text-muted mb-1" style={{ fontSize: '0.65rem' }}>
                                      Test sent - confirm receipt:
                                    </div>
                                    <div className="d-flex gap-1">
                                      <button
                                        className="btn btn-sm btn-success"
                                        onClick={() => onSendTest && onSendTest(port.id, 'confirmed')}
                                        style={{
                                          fontSize: '0.65rem',
                                          padding: '0.15rem 0.3rem',
                                        }}
                                        title="I received the test message"
                                      >
                                        ✓ Received
                                      </button>
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => onSendTest && onSendTest(port.id, 'failed')}
                                        style={{
                                          fontSize: '0.65rem',
                                          padding: '0.15rem 0.3rem',
                                        }}
                                        title="I did not receive the test message"
                                      >
                                        ✗ Not Received
                                      </button>
                                    </div>
                                  </div>
                                )}
                                {!isStaff && portTestStatus === 'confirmed' && (
                                  <span className="badge bg-success" style={{ fontSize: '0.65rem' }}>
                                    Tested ✓
                                  </span>
                                )}
                                {!isStaff && portTestStatus === 'failed' && (
                                  <span className="badge bg-danger" style={{ fontSize: '0.65rem' }}>
                                    Failed
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>

                    {/* Messages */}
                    <td>
                      {linkedPorts.length === 0 ? (
                        <span className="text-muted">—</span>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {linkedPorts.map((port) => {
                            const messages = portMessages?.[port?.id] || [];
                            return (
                              <div key={port?.id ?? `msg-${idx}`}>
                                <MessageCarousel
                                  messages={messages}
                                  portNumber={port?.assigned_number || '—'}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>

                    {/* Staff-only columns */}
                    {isStaff && (
                      <>
                        <td>
                          {linkedPorts.length > 0 ? linkedPorts[0].slack_user_id || '—' : '—'}
                        </td>
                        <td>
                          {linkedPorts.length > 0 && linkedPorts[0].last_message_sent
                            ? new Date(linkedPorts[0].last_message_sent).toLocaleDateString()
                            : '—'}
                        </td>
                        <td>
                          {linkedPorts.length > 0 && (() => {
                            const days = getDaysSinceLastMessage(linkedPorts[0].last_message_sent);
                            if (days === null) {
                              return <span className="text-muted">-</span>;
                            }
                            if (days === 0) {
                              return <span className="text-success fw-bold">Today</span>;
                            }
                            if (days === 1) {
                              return <span className="text-success">1 day</span>;
                            }
                            if (days < 7) {
                              return <span className="text-info">{days} days</span>;
                            }
                            if (days < 30) {
                              return <span className="text-warning">{days} days</span>;
                            }
                            return <span className="text-danger fw-bold">{days} days</span>;
                          })()}
                        </td>
                      </>
                    )}

                    {/* Edit - Row-based editing for user side */}
                    <td>
                      {!isStaff ? (
                        // User side: Edit button opens all fields, Save button saves all changes
                        isEditing ? (
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm btn-success"
                              onClick={async () => {
                                try {
                                  // Save email and site if changed (both use the same advice password update)
                                  // Only update if adviceId exists (advice password record exists)
                                  if (row.adviceId && onUpdateEmail && (editDraft.email !== row.email || editDraft.site !== row.site)) {
                                    // Update both email and site in one call
                                    await onUpdateEmail(row.adviceId, editDraft.email, editDraft.site);
                                  }
                                  // Save password if changed
                                  // Only update if adviceId exists (advice password record exists)
                                  if (row.adviceId && onUpdatePassword && editDraft.password !== row.password) {
                                    await onUpdatePassword(row.adviceId, editDraft.password);
                                  }
                                  // Save port details if changed (including user_email)
                                  if (linkedPorts.length > 0 && onUpdatePort) {
                                    // Update all linked ports if email, port_number, or assigned_number changed
                                    const emailChanged = editDraft.email !== row.email;
                                    for (const portToUpdate of linkedPorts) {
                                      const portData = {};
                                      let needsUpdate = false;
                                      
                                      // Update email if changed
                                      if (emailChanged && editDraft.email) {
                                        portData.user_email = editDraft.email;
                                        needsUpdate = true;
                                      }
                                      
                                      // Update port_number if changed
                                      if (editDraft.port_number !== portToUpdate.port_number) {
                                        portData.port_number = editDraft.port_number;
                                        needsUpdate = true;
                                      }
                                      
                                      // Update assigned_number if changed
                                      if (editDraft.assigned_number !== portToUpdate.assigned_number) {
                                        portData.assigned_number = editDraft.assigned_number;
                                        needsUpdate = true;
                                      }
                                      
                                      // Update target_user_code if changed (for reassignment)
                                      const newTargetUserCode = editDraft.target_user_code && editDraft.target_user_code.trim()
                                        ? editDraft.target_user_code.trim()
                                        : (currentUserCode || null);
                                      
                                      if (newTargetUserCode && newTargetUserCode !== portToUpdate.user_code) {
                                        portData.target_user_code = newTargetUserCode;
                                        needsUpdate = true;
                                      } else if (!newTargetUserCode && portToUpdate.user_code !== currentUserCode) {
                                        // Reassign to gateway owner if empty
                                        portData.target_user_code = currentUserCode || null;
                                        needsUpdate = true;
                                      }
                                      
                                      if (needsUpdate) {
                                        await onUpdatePort(portToUpdate.id, portData);
                                      }
                                    }
                                  }
                                  // Reset editing state
                                  setEditingRowId(null);
                                  setEditDraft({ email: '', password: '', port_number: '', assigned_number: '', site: '', target_user_code: '' });
                                } catch (err) {
                                  console.error('Error updating:', err);
                                  alert(err.response?.data?.detail || err.message || 'Failed to update');
                                }
                              }}
                            >
                              Save
                            </button>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => {
                                // Cancel editing - reset to original values
                                setEditingRowId(null);
                                setEditDraft({ email: '', password: '', port_number: '', assigned_number: '', site: '', target_user_code: '' });
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => {
                                // Enter edit mode - initialize draft with current values
                                // Use email as key if adviceId is not available
                                const editKey = row.adviceId || row.email || `port-${row.ports[0]?.id}`;
                                setEditingRowId(editKey);
                                setEditDraft({
                                  email: row.email || '',
                                  password: row.password || '',
                                  port_number: linkedPorts.length > 0 ? linkedPorts[0].port_number || '' : '',
                                  assigned_number: linkedPorts.length > 0 ? linkedPorts[0].assigned_number || '' : '',
                                  site: row.site || '',
                                  target_user_code: linkedPorts.length > 0 ? linkedPorts[0].user_code || '' : '',
                                });
                              }}
                              title="Edit all fields"
                            >
                              Edit
                            </button>
                            {linkedPorts.length > 0 && onDeletePort && (
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={async () => {
                                  if (window.confirm(`Are you sure you want to delete port ${linkedPorts[0].port_number || linkedPorts[0].id}? This will also delete the associated port number.`)) {
                                    try {
                                      for (const port of linkedPorts) {
                                        await onDeletePort(port.id);
                                      }
                                    } catch (err) {
                                      console.error('Error deleting port:', err);
                                      alert(err.response?.data?.detail || err.message || 'Failed to delete port');
                                    }
                                  }
                                }}
                                title="Delete port"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        )
                      ) : (
                        // Staff side: Edit port
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              if (linkedPorts.length > 0 && onEditPort) {
                                onEditPort(linkedPorts[0]);
                              }
                            }}
                          >
                            Edit
                          </button>
                          {linkedPorts.length > 0 && onDeletePort && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={async () => {
                                if (window.confirm(`Are you sure you want to delete port ${linkedPorts[0].port_number || linkedPorts[0].id}?`)) {
                                  try {
                                    for (const port of linkedPorts) {
                                      await onDeletePort(port.id);
                                    }
                                  } catch (err) {
                                    console.error('Error deleting port:', err);
                                    alert(err.response?.data?.detail || err.message || 'Failed to delete port');
                                  }
                                }
                              }}
                              title="Delete port"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <div
        ref={stickyScrollRef}
        style={{
          position: 'sticky',
          bottom: 0,
          overflowX: 'scroll',
          overflowY: 'hidden',
          height: '14px',
          zIndex: 10,
          background: '#fff',
          borderTop: '1px solid #dee2e6',
        }}
      >
        <div ref={phantomRef} style={{ height: '1px' }} />
      </div>
      <ConvertToTtOtpModal
        open={conversionModal.open}
        port={conversionModal.port}
        isStaff={isStaff}
        onClose={() => setConversionModal({ open: false, port: null })}
        onConfirm={async () => {
          const port = conversionModal.port;
          if (!port || !onRequestSimConversion) return;
          await onRequestSimConversion(port);
        }}
      />
    </div>
  );
}

